import React, { useEffect, useMemo, useReducer, useState } from "react";
import openSocket from "../../services/socket-io";
import {
  Button,
  Checkbox,
  IconButton,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import { Add, DeleteOutline, DragIndicator, Edit } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import LabelModal from "../../components/LabelModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import {
  buildWhatsappTabs,
  getWhatsappTabParams,
  orphanTabPrefix,
} from "../../utils/connectionsTabs";

const recordOwnsWhatsapp = (record, connectionId) => {
  if (!connectionId) return true;
  const selectedValue = String(connectionId);

  if (selectedValue.startsWith(orphanTabPrefix)) {
    return String(record?.orphanedConnectionId || "") ===
      selectedValue.replace(orphanTabPrefix, "");
  }

  const ids = [record?.connectionId, ...(record?.sharedConnectionIds || [])]
    .map(Number)
    .filter(id => !Number.isNaN(id));
  return ids.includes(Number(selectedValue));
};

const useStyles = makeStyles(theme => ({
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  headerButtons: {
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      marginLeft: 0,
      "& > *": {
        margin: 0,
      },
      "& .MuiButton-root": {
        minHeight: 42,
      },
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    margin: 20,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      border: 0,
      boxShadow: "none",
      backgroundColor: "transparent",
    },
  },
  colorCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  colorPreview: {
    width: 86,
    height: 24,
    borderRadius: 12,
    border: "1px solid rgba(0, 0, 0, 0.14)",
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 0),
      alignItems: "stretch",
      "& .MuiButton-root": {
        flex: "1 1 auto",
      },
    },
  },
  bulkActionsText: {
    fontWeight: 600,
    marginRight: "auto",
  },
  tableWrapper: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  mobileCards: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gap: theme.spacing(1.5),
    },
  },
  mobileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
  },
  mobileCardHeader: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mobileTitle: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  mobileMeta: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  mobileCardGrid: {
    display: "grid",
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.25),
  },
  mobileField: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minHeight: 32,
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  mobileActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
  dragHandle: {
    color: theme.palette.text.secondary,
    cursor: "grab",
    display: "inline-flex",
    "&:active": {
      cursor: "grabbing",
    },
  },
  draggingRow: {
    opacity: 0.55,
  },
  dropTarget: {
    boxShadow: `inset 0 3px 0 ${theme.palette.primary.main}`,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_LABELS") {
    const newLabels = [];
    action.payload.forEach(label => {
      const index = state.findIndex(item => item.id === label.id);
      if (index !== -1) {
        state[index] = label;
      } else {
        newLabels.push(label);
      }
    });
    return [...state, ...newLabels];
  }

  if (action.type === "UPDATE_LABEL") {
    const label = action.payload;
    const index = state.findIndex(item => item.id === label.id);
    if (index !== -1) {
      state[index] = label;
      return [...state];
    }
    return [label, ...state];
  }

  if (action.type === "SET_LABELS") return action.payload;

  if (action.type === "DELETE_LABEL") {
    return state.filter(label => label.id !== action.payload);
  }

  if (action.type === "RESET") return [];
  return state;
};

const Labels = () => {
  const classes = useStyles();
  const [labels, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [connections, setConnections] = useState([]);
  const [orphanGroups, setOrphanGroups] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [draggingLabelId, setDraggingLabelId] = useState("");
  const [dropTargetLabelId, setDropTargetLabelId] = useState("");

  const whatsappTabs = useMemo(
    () => buildWhatsappTabs(connections, orphanGroups),
    [connections, orphanGroups]
  );
  const selectedWhatsappTab = whatsappTabs.find(
    tab => String(tab.value) === String(selectedConnectionId)
  );

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: orphanData }] = await Promise.all([
          api.get("/connections/"),
          api.get("/connections/orphan-groups")
        ]);
        const loadedConnections = data || [];
        const loadedOrphans = orphanData || [];
        const tabs = buildWhatsappTabs(loadedConnections, loadedOrphans);
        setConnections(loadedConnections);
        setOrphanGroups(loadedOrphans);
        setSelectedConnectionId(prevState => prevState || tabs[0]?.value || "");
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedConnectionId) return;

    (async () => {
      setLoading(true);
      dispatch({ type: "RESET" });
      setSelectedLabelIds([]);
      try {
        const { data } = await api.get("/labels", {
          params: getWhatsappTabParams(selectedConnectionId),
        });
        dispatch({ type: "LOAD_LABELS", payload: data });
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    })();
  }, [selectedConnectionId, refreshKey]);

  useEffect(() => {
    const labelIds = new Set(labels.map(label => String(label.id)));
    setSelectedLabelIds(prevState =>
      prevState.filter(labelId => labelIds.has(String(labelId)))
    );
  }, [labels]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("label", data => {
      if (data.action === "reorder") {
        setRefreshKey(current => current + 1);
        return;
      }
      if (data.action === "update" || data.action === "create") {
        if (
          !selectedConnectionId ||
          recordOwnsWhatsapp(data.label, selectedConnectionId)
        ) {
          dispatch({ type: "UPDATE_LABEL", payload: data.label });
        } else if (data.action === "update") {
          dispatch({ type: "DELETE_LABEL", payload: data.label.id });
        }
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_LABEL", payload: data.labelId });
      }
    });
    return () => socket.disconnect();
  }, [selectedConnectionId]);

  const handleCloseLabelModal = () => {
    setLabelModalOpen(false);
    setSelectedLabel(null);
  };

  const handleDeleteLabel = async labelId => {
    try {
      await api.delete(`/labels/${labelId}`);
      toast.success(i18n.t("labels.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setSelectedLabel(null);
    setConfirmModalOpen(false);
  };

  const allLabelsSelected =
    labels.length > 0 &&
    labels.every(label => selectedLabelIds.includes(String(label.id)));
  const selectedLabelCount = selectedLabelIds.length;

  const toggleLabelSelection = labelId => {
    const normalizedId = String(labelId);
    setSelectedLabelIds(prevState =>
      prevState.includes(normalizedId)
        ? prevState.filter(id => id !== normalizedId)
        : [...prevState, normalizedId]
    );
  };

  const toggleAllLabelsSelection = () => {
    if (allLabelsSelected) {
      setSelectedLabelIds([]);
      return;
    }

    setSelectedLabelIds(labels.map(label => String(label.id)));
  };

  const handleDeleteSelectedLabels = async () => {
    try {
      await Promise.all(
        selectedLabelIds.map(labelId => api.delete(`/labels/${labelId}`))
      );
      selectedLabelIds.forEach(labelId =>
        dispatch({ type: "DELETE_LABEL", payload: Number(labelId) })
      );
      setSelectedLabelIds([]);
      toast.success(i18n.t("labels.toasts.deleted"));
    } catch (err) {
      toastError(err);
    } finally {
      setBulkDeleteOpen(false);
    }
  };

  const persistLabelOrder = async nextLabels => {
    dispatch({ type: "SET_LABELS", payload: nextLabels });
    try {
      const { data } = await api.put("/labels/reorder", {
        labelIds: nextLabels.map(label => label.id),
      });
      dispatch({
        type: "SET_LABELS",
        payload: (data.labels || []).filter(label =>
          recordOwnsWhatsapp(label, selectedConnectionId)
        ),
      });
    } catch (err) {
      toastError(err);
      setRefreshKey(current => current + 1);
    }
  };

  const moveLabel = targetLabelId => {
    const sourceIndex = labels.findIndex(
      label => String(label.id) === String(draggingLabelId)
    );
    const targetIndex = labels.findIndex(
      label => String(label.id) === String(targetLabelId)
    );
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return;

    const nextLabels = [...labels];
    const [movedLabel] = nextLabels.splice(sourceIndex, 1);
    nextLabels.splice(targetIndex, 0, movedLabel);
    persistLabelOrder(nextLabels);
  };

  const renderLabelActions = label => (
    <>
      <IconButton
        size="small"
        onClick={() => {
          setSelectedLabel(label);
          setLabelModalOpen(true);
        }}
      >
        <Edit />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => {
          setSelectedLabel(label);
          setConfirmModalOpen(true);
        }}
      >
        <DeleteOutline />
      </IconButton>
    </>
  );

  const renderMobileLabelCard = label => (
    <div className={classes.mobileCard} key={label.id}>
      <div className={classes.mobileCardHeader}>
        <Checkbox
          size="small"
          color="primary"
          checked={selectedLabelIds.includes(String(label.id))}
          onChange={() => toggleLabelSelection(label.id)}
          inputProps={{ "aria-label": `Seleccionar ${label.name}` }}
        />
        <div>
          <Typography className={classes.mobileTitle}>{label.name}</Typography>
          <Typography className={classes.mobileMeta}>
            {label.connection?.name || label.connection?.number || "-"}
          </Typography>
        </div>
        <div className={classes.mobileActions}>{renderLabelActions(label)}</div>
      </div>

      <Divider />

      <div className={classes.mobileCardGrid}>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Conexión</span>
          <Typography variant="body2">
            {label.connection?.name || label.connection?.number || "-"}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>{i18n.t("labels.table.color")}</span>
          <span className={classes.colorPreview} style={{ backgroundColor: label.color }} />
        </div>
      </div>
    </div>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={selectedLabel && `${i18n.t("labels.confirmationModal.deleteTitle")} ${selectedLabel.name}?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteLabel(selectedLabel.id)}
      >
        {i18n.t("labels.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title={`Eliminar ${selectedLabelCount} etiqueta${
          selectedLabelCount === 1 ? "" : "s"
        }?`}
        open={bulkDeleteOpen}
        onClose={setBulkDeleteOpen}
        onConfirm={handleDeleteSelectedLabels}
      >
        {i18n.t("labels.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <LabelModal
        open={labelModalOpen}
        onClose={handleCloseLabelModal}
        labelId={selectedLabel?.id}
        defaultConnectionId={selectedConnectionId}
      />
      <MainHeader>
        <div className={classes.headerContent}>
          <Title>{i18n.t("labels.title")}</Title>
          <MainHeaderButtonsWrapper className={classes.headerButtons}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              disabled={!selectedConnectionId || selectedWhatsappTab?.orphan}
              onClick={() => setLabelModalOpen(true)}
            >
              {i18n.t("labels.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs
          value={selectedConnectionId}
          onChange={(event, value) => setSelectedConnectionId(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {whatsappTabs.length ? (
            whatsappTabs.map(whatsapp => (
              <Tab
                key={whatsapp.value}
                value={whatsapp.value}
                label={whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
              />
            ))
          ) : (
            <Tab value="" label="Sin conexiones" disabled />
          )}
        </Tabs>
        {labels.length > 0 && (
          <div className={classes.bulkActions}>
            <Checkbox
              size="small"
              color="primary"
              checked={allLabelsSelected}
              indeterminate={selectedLabelCount > 0 && !allLabelsSelected}
              onChange={toggleAllLabelsSelection}
              inputProps={{ "aria-label": "Seleccionar etiquetas" }}
            />
            <Typography variant="body2" className={classes.bulkActionsText}>
              {selectedLabelCount
                ? `${selectedLabelCount} seleccionada${
                    selectedLabelCount === 1 ? "" : "s"
                  }`
                : "Seleccionar etiquetas"}
            </Typography>
            {selectedLabelCount > 0 && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedLabelIds([])}
                >
                  Limpiar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteOutline />}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Eliminar
                </Button>
              </>
            )}
          </div>
        )}
        <div className={classes.mobileCards}>
          {labels.map(renderMobileLabelCard)}
          {loading && <CircularProgress size={24} />}
        </div>

        <Table size="small" className={classes.tableWrapper}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell padding="checkbox" />
              <TableCell align="center">{i18n.t("labels.table.name")}</TableCell>
              <TableCell align="center">Conexión</TableCell>
              <TableCell align="center">{i18n.t("labels.table.color")}</TableCell>
              <TableCell align="center">{i18n.t("labels.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {labels.map(label => (
              <TableRow
                key={label.id}
                className={`${String(draggingLabelId) === String(label.id) ? classes.draggingRow : ""} ${String(dropTargetLabelId) === String(label.id) ? classes.dropTarget : ""}`}
                onDragOver={event => {
                  if (!draggingLabelId || String(draggingLabelId) === String(label.id)) return;
                  event.preventDefault();
                  setDropTargetLabelId(String(label.id));
                }}
                onDrop={event => {
                  event.preventDefault();
                  moveLabel(label.id);
                  setDraggingLabelId("");
                  setDropTargetLabelId("");
                }}
              >
                <TableCell padding="checkbox">
                  <span
                    className={classes.dragHandle}
                    draggable
                    onDragEnd={() => {
                      setDraggingLabelId("");
                      setDropTargetLabelId("");
                    }}
                    onDragStart={event => {
                      event.dataTransfer.effectAllowed = "move";
                      setDraggingLabelId(String(label.id));
                    }}
                    title="Arrastrar etiqueta"
                  >
                    <DragIndicator fontSize="small" />
                  </span>
                </TableCell>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={selectedLabelIds.includes(String(label.id))}
                    onChange={() => toggleLabelSelection(label.id)}
                    inputProps={{ "aria-label": `Seleccionar ${label.name}` }}
                  />
                </TableCell>
                <TableCell align="center">{label.name}</TableCell>
                <TableCell align="center">
                  {label.connection?.name || label.connection?.number || "-"}
                </TableCell>
                <TableCell align="center">
                  <div className={classes.colorCell}>
                    <span
                      className={classes.colorPreview}
                      style={{ backgroundColor: label.color }}
                    />
                  </div>
                </TableCell>
                <TableCell align="center">
                  {renderLabelActions(label)}
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={5} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Labels;

